import {get} from "lodash";

import appConfig, {channelEnum} from "../appconfig";
import {DomainCoreGeneration} from "../domains2/libraries/DomainCoreGeneration";
import {formatPriceZero, formatPriceZeroWithTotalPriceAndQuantity} from "../domains2/libraries/Utils";
import ProductSelection, {mapProductSelection} from "../domains2/orders/ProductSelection";
import {OrderLineType} from "../domains2/orders/OrderLine2";
import * as actions from "../store/mutation-types";
import * as Utils from "../libraries/utils";
import {getGTMFlattenSingleProductSelection} from "../integrations/tracking/gtm/gtmHelper";
import {pushGTMeCommerceData} from "../integrations/tracking/gtm/pushGTMeCommerceData";
import OrderHelperObject from "../domains2/orders/OrderHelperObject";
import HelloRetail from "../integrations/recommendations/helloRetail/HelloRetail";
import {noThanksObject} from "../helper/ProductSelectionHelper";
import {isCurrentSelectionAccessible} from "../libraries/globalTemplateSpecific/bm100/utils";

const tenantConfig = appConfig.tenantConfig;

// temporary exclude descriptions which includes this text
const forbiddenItems = ["Tilføj drikkevare"];

export default {
	data() {
		return {
			removingItem: false,
			timeoutHandle: null,
			cartQuantityDialogShown: false,
			dialogOpened: false,
			dialogText: "",
			currentConfirmAction: null,
			removeItemFromBasket: false,
			removeItemFromQuantity: false
		};
	},
	mounted() {
		this.quantityAlwaysVisible();
	},
	computed: {
		isGeneration5() {
			return DomainCoreGeneration.Generation5 === this.receiptLine.generation;
		},
		productQuantity() {
			return parseInt(this.receiptLine.quantity);
		},
		menuSize() {
			return this.receiptLine.menuSize || this.receiptLine.menuSize !== "" ? this.receiptLine.menuSize : "";
		},
		productSelectionIds() {
			if (this.orderLine.type === "CombinatorLine") {
				return this.orderLine.subLines.map(line => line.productSelectionId);
			}

			return [this.orderLine.productSelectionId];
		},
		isSimpleProduct() {
			const ol = this.orderLine;
			return ol.type !== OrderLineType.Aggregation;
		},
		hasConfigurationOrGroup() {
			return get(this.productSelection, "groups", []).length > 0
				|| get(this.productSelection, "configurations", []).length > 0;
		},
		isMobileChannel() {
			return appConfig.channelAlias() === channelEnum.mobileBrowser || appConfig.channelAlias() === channelEnum.mobileApp;
		},
		isBM100() {
			return appConfig.isBM100();
		},
		getVoucherExpiresAt() {
			if(!this.$store.getters.voucherReservationTime) return "";
			const voucher = this.receiptLine.highlightDescription.find(desc => desc.voucherId);
			if(voucher) {
				const expirationTime = this.$store.getters.voucherReservationTime[voucher.voucherId];
				return expirationTime && expirationTime.time
					? this.$t("shopComponents.gt2.shoppingCart.orderItem.voucherExpiresAt", {
						time: expirationTime.time.slice(-8, -3) // take only time
					})
					: "";
			}
			return "";
		}
	},
	methods: {
		formatPriceZero,
		formatPriceZeroWithTotalPriceAndQuantity,
		realSubproductsOnly(selectedProducts) {
			return selectedProducts.filter(selectedProduct => selectedProduct !== noThanksObject.id);
		},
		async changeQuantity(valueSent, isIncreased = false) {
			// window.logTime = Date.now();
			// console.log(`Plus button ${window.logTime}`);

			let value = +valueSent || 0;
			const displayQuantity = +(this.productSelection && (this.productSelection.quantityWithoutCombinator || this.productSelection.quantity));
			const actualQuantity = +(this.productSelection && this.productSelection.actualQuantity);
			if (Number.isFinite(displayQuantity) && Number.isFinite(actualQuantity)) {
				value = actualQuantity + (value - displayQuantity);
			}

			if (value > 0) {
				if (this.hasConfigurationOrGroup) {
					// get groups and configurations selected values inside main selected product
					const productGroupSelection = this.productSelection.groups;
					const productConfigSelection = this.productSelection.configurations;
					const psOptions = {
						expiration: {
							tenantSettings: this.tenantSettings.productSelectionExpiration,
							tenantConfig: tenantConfig.productSelectionExpiration
						}
					};
					// create new product selection
					const newProductSelection = ProductSelection.createFromProduct(this.product, psOptions);
					ProductSelection.ChangeQuantity(newProductSelection, this.product, 1);
					// assign selected values inside groups and configs to product selection
					newProductSelection.groups = productGroupSelection;
					newProductSelection.configurations = productConfigSelection;
					newProductSelection.forceNewInCart = true;
					const mappedNewSelections = mapProductSelection(this.productCatalog, [newProductSelection], this.realSubproductsOnly);
					const isCurrentSelectionAvailable = isCurrentSelectionAccessible(mappedNewSelections, this);
					if(!isCurrentSelectionAvailable) {
						return;
					}
					// add selection to cart
					await this.$store.dispatch(actions.ADD_PRODUCT_SELECTION_TO_CART, mappedNewSelections[0]);
				} else {
					const mappedSelections = mapProductSelection(this.productCatalog, [this.productSelection], this.realSubproductsOnly);
					const isCurrentSelectionAvailable = isCurrentSelectionAccessible(mappedSelections, this);
					if(!isCurrentSelectionAvailable) {
						return;
					}
					await this.$store.dispatch(actions.UPDATE_CART_QUANTITY_BY_ID, {
						productSelectionId: mappedSelections[0].productSelectionId,
						quantity: value
					});
				}
				HelloRetail.cartTracking(this);
				try {
					const mainProduct = OrderHelperObject.mapToReceiptLines([this.orderLine], this.$i18n)[0];
					const productWithSubProducts = getGTMFlattenSingleProductSelection(mainProduct);
					const products = productWithSubProducts.length ? productWithSubProducts : [{product: this.product}];
					pushGTMeCommerceData({
						eventName: isIncreased ? "add_to_cart" : "remove_from_cart",
						products: products,
						value: this.receiptLine.totalPriceAccum,
						category: typeof this.findCategory === "function" ? this.findCategory(this.orderLine.productId) : undefined
					});
				} catch (e) {
					// even if something fails in this block, we should proceed
				}
			} else {
				if(this.isBM100) {
					this.protectRemoveItem(() => this.removeFromCart(), "shopComponents.gt2.shoppingCart_steps.shoppingCartStepCheckoutInit.removeItemQuantity", false, true);
				} else {
					this.removeFromCart();
				}
			}
			if(this.isBM100) {
				this.quantityAlwaysVisible();
			} else {
				this.resetTimeout();
			}
		},
		findCategory(productId) {
			const self = this;
			const categories = Utils.getCategoriesByShopId(
				self.$store.getters.globalSelectedShop.id,
				self.productCatalog
			);
			return categories.find((cat) => {
				return (
					cat.elements.find((element) => {
						if (element.objType === "GridBlockElement") {
							return (
								element.items.find((product) => product.id === productId) !== undefined
							);
						} else if (element.props && element.props.products) {
							return (
								element.props.products.find((product) => product === productId) !== undefined
							);
						} else {
							return false;
						}
					}) !== undefined
				);
			});
		},
		removeFromCart() {
			this.removingItem = true;
			if(this.$store.getters.selectedDealId === this.orderLine.productId) {
				this.$store.commit(actions.SET_SELECTED_DEAL_ID);
			}
			window.setTimeout(() => Promise
				.all(this.productSelectionIds
					.map(id => this.$store.dispatch(actions.REMOVE_FROM_CART_BY_PRODUCT_SELECTION_ID, id))
				)
				.then(() => {
					this.removingItem = false;
					this.cartQuantityDialogShown = !!(this.isBM100 && this.isMobileChannel);
					HelloRetail.cartTracking(this);
				}), 0);
			try {
				const mainProduct = OrderHelperObject.mapToReceiptLines([this.orderLine], this.$i18n)[0];
				const productWithSubProducts = getGTMFlattenSingleProductSelection(mainProduct);
				const products = productWithSubProducts.length ? productWithSubProducts : [{product: this.product}];
				pushGTMeCommerceData({
					eventName: "remove_from_cart",
					products: products,
					value: this.receiptLine.totalPriceAccum,
					category: typeof this.findCategory === "function" ? this.findCategory(this.orderLine.productId) : undefined
				});
			} catch (e) {
				// even if something fails in this block, we should proceed
			}
		},
		resetTimeout() {
			window.clearTimeout(this.timeoutHandle);
			this.timeoutHandle = window.setTimeout(() => {
				this.cartQuantityDialogShown = false;
			}, 3000);
		},
		openQuantityPopUp() {
			this.cartQuantityDialogShown = true;
			this.resetTimeout();
		},
		includedPrice() {
			const includedPrice = get(this.orderLine, "options.foreignIds.onlinePosRules.cart", [])
				.find((c) => c.inclPrice);
			return includedPrice ? includedPrice.inclPrice : -1;
		},
		includedPriceName() {
			const includedPrice = get(this.orderLine, "options.foreignIds.onlinePosRules.cart", [])
				.find((c) => c.inclPrice);
			return includedPrice ? includedPrice.name : "";
		},
		isForbidden(str) {
			return forbiddenItems.find(item => item.includes(str)) !== undefined;
		},
		getProduct(productId) {
			return this.productCatalog.getProduct(productId);
		},
		protectRemoveItem(action, text, removeFromBasket, removeFromQuantity) {
			if(this.isBM100 && this.$store.getters.featureToggles.enableProtectOrderingFlow) {
				this.dialogText = text;
				this.dialogOpened = true;
				this.currentConfirmAction = action;
				this.removeItemFromBasket = removeFromBasket;
				this.removeItemFromQuantity = removeFromQuantity;
			} else {
				return action();
			}
		},
		closeConfirmDialog() {
			this.dialogOpened = false;
		},
		quantityAlwaysVisible() {
			if(this.isBM100 && this.isMobileChannel) {
				this.cartQuantityDialogShown = true;
			}
		}
	}
};
